import { Component, OnInit, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Data, Event, NavigationEnd, ActivatedRoute, Router } from '@angular/router';
import { ImagesCmsService } from '@services/images-cms.service';
import { ErrorInt, PCCInt } from '@interfaces/';
import { Destinations } from '@models/destinations.model';
import { LocalDataService } from '@services/local-data.service';
import { Airports, LoyaltyPrograms } from '@models/index';
import { AirportsService } from '@services/airports.service';
import { ApiService } from '@services/api.service';
import { CussService } from '@services/cuss.service';
import { AdminService } from '@services/admin.service';
import { Admin } from '@app/enums';
import { filter, map, mergeMap } from 'rxjs';
import { UrlService } from './services/url.service';
import { Notices } from '@models/mapCMS.model';
import { KioskPropertiesGA4Int } from './interfaces/ga4.interface';
import { Ga4Service } from './services/ga4.service';
import { PaymentService } from '@services/payment.service';
import { GlobalsService as GlobalsServiceMicro } from '@services/micro/globals.service';
import { HIDDEN_ROUTES_ON_VERTICAL_KIOSK } from './constants/kiosk-resolution-routes.constant';
import { ConnectivityMonitoringService } from './services/connectivity-monitoring.service';
import { NewRelicEventsService } from './services/new-relic-events.service';
import { QualtricsRouteWatcherService } from './services/qualtrics/qualtrics-route-watcher.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'kioskos';
  isReady = false;
  previousUrl: string;
  currentUrl: string;
  restrictionSubFunnels = ['reservation_status_delay', 'SR_select_flight', 'privacy_policy_info'];

  constructor(
    private _router: Router,
    private cussServ: CussService,
    private translate: TranslateService,
    private imageService: ImagesCmsService,
    private localServ: LocalDataService,
    private airportServ: AirportsService,
    private apiServ: ApiService,
    private adminServ: AdminService,
    private _urlService: UrlService,
    private _activatedRoute: ActivatedRoute,
    private _ga4Service: Ga4Service,
    private _payment: PaymentService,
    private _globalMicro: GlobalsServiceMicro,
    _connectivityMonitoring: ConnectivityMonitoringService,
    private _newRelicEventsService: NewRelicEventsService,
    private _qualtricsRouteWatcher: QualtricsRouteWatcherService
  ) {
    const idByURL = this.getKioskIDByURL();

    if (idByURL) {
      cussServ.KioskID = idByURL;
      this.getKioskProfile(idByURL);
    } else {
      this.findKioksID();
    }
    translate.use('es-MX');
    this.loadAirports();
    this.loadDestinations();
    this.loadLoyaltyPrograms();
    this.getBagsRules();
    this.loadNotice();
    this.loadTimaticType();
    if (this.localServ.countries) {
      this.loadCountries();
    }
  }

  ngOnDestroy(): void {}

  ngOnInit(): void {
    this.initListenUrl();
    this.getPCC();
    // CI-23287: monta/desmonta la encuesta Qualtrics según la ruta. No hace nada si el flag
    // `environment.qualtrics.enabled` está apagado.
    this._qualtricsRouteWatcher.start();
  }

  initListenUrl() {
    this._router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: Event) => {
        if (!(event instanceof NavigationEnd)) {
          return;
        }
        const route = event.urlAfterRedirects || event.url;
        if (this.isHomeRoute(route)) {
          this._newRelicEventsService.completeSessionFlowTimer('home');
        }

        this.previousUrl = this.currentUrl;
        this.currentUrl = event.url;
        this._urlService.setPreviousUrl(this.previousUrl);

        this.getDataForMetric().then((data: any) => {
          this._urlService.currentUrl = this.currentUrl || 'NA';
          this._urlService.beforeUrl = this.previousUrl;

          if (!this.restrictionSubFunnels.includes(data?.subFunnel)) {
            this._urlService.subFunnelName = data?.subFunnel || 'NA';
            this.sendEventPropertiesGa4(data?.subFunnel);
          }
        });
        if (this.shouldHideRouteForCurrentKiosk(route)) {
          this._router.navigate(['/']);
          return;
        }
      });
  }

  loadLoyaltyPrograms() {
    let response: any;
    this.localServ.getLoyaltyPrograms().subscribe({
      next: (res) => {
        response = res;
      },
      error: (error: ErrorInt) => {},
      complete: () => {
        LoyaltyPrograms.setloyaltyProgram(response);
      },
    });
  }

  loadDestinations() {
    this.imageService.getArrivalImages().subscribe({
      next: (res) => {
        Destinations.setDestinations(res);
      },
      error: (error: ErrorInt) => {},
      complete: () => {},
    });
  }

  loadAirports() {
    let response: any;

    this.airportServ.loadAirports().subscribe({
      next: (res) => {
        response = res;
      },
      error: (error: ErrorInt) => {},
      complete: () => {
        Airports.setAirports(response);
      },
    });
  }

  loadCountries() {
    this.localServ.getCountries().subscribe({
      next: (countries) => {
        this.localServ.countries = countries;
      },
      error: () => {},
    });
  }

  loadTimaticType() {
    this.localServ.getTimaticStay().subscribe({
      next: (timaticType) => {
        this.localServ.timaticType = timaticType;
      },
      error: () => {},
    });
  }
  loadNotice() {
    this.imageService.loadCMSNoticeImages().subscribe({
      next: (res) => {
        Notices.setNotices(res);
      },
    });
  }

  getBagsRules() {
    this.localServ.getBagsRules().subscribe({
      next: (bagRules) => {
        this.localServ.bagRules = bagRules;
      },
      error: () => {},
    });
  }

  getKioskProfile(KioskID: string) {
    this.adminServ.getKioskProfile(KioskID).subscribe({
      next: (res) => {
        this.adminServ.admin = res;
        this.applyKioskResolutionClass();
        this.translate.use(res.DEFAULT_LANGUAGE);
        setTimeout(() => {
          this.isReady = true;
        }, 0);
      },
      error: (err) => {
        this.adminServ.admin = {
          bagTag: true,
          DOCV: true,
          HANDLE_OVERWEIGHT_BAG: false,
          noPassportScanner: false,
          outOfService: false,
          Payment: true,
          scale: true,
          SHOW_OVERBOOKING_VOLUNTEER_OFFER: false,
          SHOW_PREMIERE_OFFER: false,
          SHOW_SEAT_SELECT_PREMIERE_OFFER: false,
          TSA: false,
          WAIT_FOR_AGENT: false,
          AM_PLUS_UPSELL: false,
          Payment_And_Weighing_Test: false,
          INTERNATIONALROUTES: true,
          USA_REQUIREMENTS: false,
          showIrops: false,
          showUSPerson: true,
          showSeatMap: true,
          iropReaccommed: false,
          DEFAULT_LANGUAGE: 'es-MX',
          Location: 'MEX',
          currency: 'MXN',
          currencyCode: 'MXN',
          store: 'mx',
          maletas25KG: true,
          country: 'MX',
          enableSpecialColorLabels: false,
        } as any;
        this.translate.use('es-MX');
        setTimeout(() => {
          this.isReady = true;
        }, 0);
      },
      complete: () => {},
    });
  }

  findKioksID() {
    this.cussServ.getKioskInfo().subscribe({
      next: (id) => {
        if (id) {
          this.cussServ.KioskID = id;
          this.getKioskProfile(id);
        }
      },
    });
  }

  getKioskIDByURL(): string {
    const url = window.location.href;
    const params = url.split('?');
    const multipleParams =
      params && params.length > 1
        ? params[1].split('&').map((value) => {
            const elemnt = value.split('=');
            const map = new Map();
            return map.set(elemnt[0], elemnt[1]);
          })
        : [];
    return multipleParams && multipleParams.length > 0 ? multipleParams[0].get('KioskID') : null;
  }

  getDataForMetric(): Promise<any> {
    return new Promise((resolve) => {
      this._router.events
        .pipe(
          filter((event) => event instanceof NavigationEnd),
          map(() => this._activatedRoute),
          map((route) => {
            while (route.firstChild) {
              route = route.firstChild;
            }
            return route;
          }),
          mergeMap((route) => route.data)
        )
        .subscribe((data) => {
          resolve(data);
        });
    });
  }

  sendEventPropertiesGa4(subFunnelName) {
    const kioskPropertiesGA4: KioskPropertiesGA4Int = {
      page_location: this.currentUrl,
      screen_resolution: this.getScreenResolution(),
      sub_funnel_name: subFunnelName,
    };

    if (kioskPropertiesGA4.page_location == '/home' || kioskPropertiesGA4.page_location == '/') {
      if (this._ga4Service.nameSession) {
        this._ga4Service.sendSessionEndEvent('session_end_am', 'home');
      }
    }
    this._ga4Service.sendKioskPropertiesEvent(kioskPropertiesGA4);
  }

  getPCC() {
    this._payment.getPCC().subscribe({
      next: (res) => {
        this._globalMicro.pcc = res as PCCInt;
      },
      error: (error) => {
        this.adminServ.admin.Payment = false;
      },
    });
  }

  private getScreenResolution(): string {
    return this.isVerticalKiosk() ? '1080x1920' : '1280x1024';
  }

  private isVerticalKiosk(): boolean {
    return Boolean(this.adminServ.admin?.[Admin.KIOSK_VERTICAL_SCREEN]);
  }

  private applyKioskResolutionClass(): void {
    const body = document.body;
    const isVertical = this.isVerticalKiosk();
    body.classList.toggle('kiosk-vertical', isVertical);
    body.classList.toggle('kiosk-horizontal', !isVertical);
  }

  private shouldHideRouteForCurrentKiosk(url: string): boolean {
    if (!this.isVerticalKiosk()) {
      return false;
    }

    return HIDDEN_ROUTES_ON_VERTICAL_KIOSK.some(
      (hiddenRoute) => url === hiddenRoute || url.startsWith(`${hiddenRoute}/`)
    );
  }

  private isHomeRoute(url: string): boolean {
    const cleanUrl = (url || '').split('?')[0].split('#')[0];
    return cleanUrl === '/' || cleanUrl === '/home' || cleanUrl === '';
  }
}
